import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  Button,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tooltip,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import {
  ViewIcon,
  DeleteIcon,
  CheckCircleIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

const ProspectListCard = ({ list, onDelete, onUpdate }) => {
  const navigate = useNavigate();

  const getProgressColor = () => {
    const percentage = (list.contacted_places / list.total_places) * 100;
    if (percentage >= 80) return "green";
    if (percentage >= 50) return "yellow";
    return "red";
  };

  const getStatusBadge = () => {
    if (list.is_completed) {
      return (
        <Badge colorScheme="green" variant="solid">
          <CheckCircleIcon mr={1} />
          Completa
        </Badge>
      );
    }
    return (
      <Badge colorScheme="orange" variant="solid">
        <TimeIcon mr={1} />
        Em andamento
      </Badge>
    );
  };

  const handleViewDetails = () => {
    navigate(`/prospeccao/${list.id}`);
  };

  const conversionRate = list.contacted_places > 0
    ? Math.round((list.converted_places / list.contacted_places) * 100)
    : 0;

  const contactRate = list.total_places > 0
    ? Math.round((list.contacted_places / list.total_places) * 100)
    : 0;

  return (
    <Card
      shadow="md"
      _hover={{
        shadow: "lg",
        transform: "translateY(-2px)",
        transition: "all 0.2s",
      }}
    >
      <CardHeader pb={2}>
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={1} flex={1}>
            <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
              {list.name}
            </Text>
            <Text color="gray.600" fontSize="sm" noOfLines={2}>
              "{list.search_query}"
            </Text>
          </VStack>
          {getStatusBadge()}
        </HStack>
      </CardHeader>

      <CardBody py={4}>
        <VStack spacing={4} align="stretch">
          {/* Progress Bar */}
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">
                Progresso de Contato
              </Text>
              <Text fontSize="sm" fontWeight="semibold" color={`${getProgressColor()}.500`}>
                {contactRate}%
              </Text>
            </HStack>
            <Progress
              value={contactRate}
              colorScheme={getProgressColor()}
              size="sm"
              borderRadius="full"
            />
            <Text fontSize="xs" color="gray.500">
              {list.contacted_places} de {list.total_places} lugares contactados
            </Text>
          </VStack>

          {/* Estatísticas */}
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <Stat size="sm">
                <StatLabel fontSize="xs">Conversões</StatLabel>
                <StatNumber fontSize="lg" color="green.500">
                  {list.converted_places}
                </StatNumber>
                <StatHelpText fontSize="xs">
                  {conversionRate}% taxa
                </StatHelpText>
              </Stat>
            </GridItem>

            <GridItem>
              <Stat size="sm">
                <StatLabel fontSize="xs">Pendentes</StatLabel>
                <StatNumber fontSize="lg" color="orange.500">
                  {list.total_places - list.contacted_places}
                </StatNumber>
                <StatHelpText fontSize="xs">
                  para contato
                </StatHelpText>
              </Stat>
            </GridItem>
          </Grid>

          {/* Informações Adicionais */}
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.500">
                Criado por:
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {list.creator?.name || 'Usuário'}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.500">
                Criado em:
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {format(new Date(list.created_at), 'dd/MM/yyyy', { locale: ptBR })}
              </Text>
            </HStack>

            {list.updated_at !== list.created_at && (
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  Atualizado em:
                </Text>
                <Text fontSize="xs" fontWeight="medium">
                  {format(new Date(list.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </Text>
              </HStack>
            )}
          </VStack>
        </VStack>
      </CardBody>

      <CardFooter pt={2}>
        <HStack spacing={2} width="100%">
          <Button
            leftIcon={<ViewIcon />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            flex={1}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails();
            }}
          >
            Ver Detalhes
          </Button>

          <Tooltip label="Excluir Lista" placement="top">
            <IconButton
              icon={<DeleteIcon />}
              colorScheme="red"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(list.id);
              }}
            />
          </Tooltip>
        </HStack>
      </CardFooter>
    </Card>
  );
};

export default ProspectListCard;